import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, ChevronDown, Check } from "lucide-react";
import { LOCALES } from "@/i18n/locales";
import { useLocale } from "@/i18n/LocaleContext";

// Sprachmenü: Button + Liste mit nativen Sprachnamen, Tastatur- und Screenreader-tauglich
export default function LanguageSwitcher({ variant = "desktop" }) {
  const { code, pathIn, t } = useLocale();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onDown = (e) => ref.current && !ref.current.contains(e.target) && setOpen(false);
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (variant === "mobile") {
    return (
      <div className="mt-4">
        <p className="px-4 text-sm font-600 uppercase tracking-wide text-muted-foreground">{t.common.language}</p>
        <ul className="mt-2 grid grid-cols-2 gap-1">
          {LOCALES.map((l) => (
            <li key={l.code}>
              <Link
                to={pathIn(l.code)}
                lang={l.code}
                dir={l.dir}
                hrefLang={l.code}
                aria-current={l.code === code ? "true" : undefined}
                className={`flex items-center justify-between rounded-xl px-4 py-2.5 text-base font-600 transition-colors ${
                  l.code === code ? "bg-brand-light text-brand" : "text-ink hover:bg-brand-light/60"
                }`}
              >
                {l.name}
                {l.code === code && <Check className="w-4 h-4" aria-hidden="true" />}
              </Link>
            </li>
          ))}
        </ul>
      </div>
    );
  }

  const current = LOCALES.find((l) => l.code === code);
  return (
    <div ref={ref} className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={t.common.changeLanguage}
        data-testid="language-switcher"
        className="inline-flex items-center gap-1.5 rounded-xl px-3 py-2 text-base font-600 text-ink hover:bg-brand-light/60 focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-brand/40"
      >
        <Globe className="w-5 h-5 text-brand" aria-hidden="true" />
        <span>{current.code.toUpperCase()}</span>
        <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {open && (
        <ul
          role="listbox"
          aria-label={t.common.language}
          className="absolute end-0 mt-2 w-48 rounded-2xl border border-border bg-white p-2 shadow-[0_12px_40px_rgb(44,51,51,0.14)] z-50"
        >
          {LOCALES.map((l) => (
            <li key={l.code} role="option" aria-selected={l.code === code}>
              <Link
                to={pathIn(l.code)}
                lang={l.code}
                dir={l.dir}
                hrefLang={l.code}
                onClick={() => setOpen(false)}
                className={`flex items-center justify-between rounded-xl px-3 py-2 text-base transition-colors ${
                  l.code === code ? "bg-brand-light text-brand font-600" : "text-ink hover:bg-brand-light/60"
                }`}
              >
                {l.name}
                {l.code === code && <Check className="w-4 h-4" aria-hidden="true" />}
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
